// apps/nos/client/src/components/Sidebar/SidebarRecentList.tsx
import { useEffect, useRef, type RefObject } from "react";
import { useRecentFlavors } from "@/hooks/useSidebarFlavors";
import { formatRelative } from "@/utils/formatRelative";
import type { SidebarFlavor } from "@/utils/types/flavor";
import { SidebarFlavorRow } from "./SidebarFlavorRow";

type Props = {
  scrollRef: RefObject<HTMLDivElement | null>;
  starredCount: number;
  activeId?: string;
  onSelect: (f: SidebarFlavor) => void;
};

// Sentinel hook — fires `onIntersect` whenever the sentinel enters view.
// Uses `rootMargin: '64px 0px'` so we prefetch slightly before the user reaches
// the visible boundary. `scrollRef` is the scroll container (not document).
function useSentinel(
  scrollRef: RefObject<HTMLDivElement | null>,
  onIntersect: () => void,
): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);
  // Keep the latest callback in a ref so the observer doesn't churn.
  const cbRef = useRef(onIntersect);
  cbRef.current = onIntersect;

  useEffect(() => {
    const root = scrollRef.current;
    const target = ref.current;
    if (!root || !target) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) cbRef.current();
        }
      },
      { root, rootMargin: "64px 0px", threshold: 0 },
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [scrollRef]);

  return ref;
}

export function SidebarRecentList({
  scrollRef,
  starredCount,
  activeId,
  onSelect,
}: Props) {
  const q = useRecentFlavors();

  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const isInitialEmpty =
    !q.isLoading && items.length === 0 && !q.hasNextPage && !q.hasPreviousPage;

  const topSentinelRef = useSentinel(scrollRef, () => {
    if (q.hasPreviousPage && !q.isFetchingPreviousPage) q.fetchPreviousPage();
  });
  const bottomSentinelRef = useSentinel(scrollRef, () => {
    if (q.hasNextPage && !q.isFetchingNextPage) q.fetchNextPage();
  });

  // Combined empty case: no Starred AND no Recent → show single placeholder line.
  // Per spec §7.2: "You will see your flavors here" only when BOTH lists are empty.
  if (isInitialEmpty && starredCount === 0) {
    return (
      <div className="mono sidebar-empty" style={{ textAlign: "center" }}>
        You will see your flavors here
      </div>
    );
  }

  // Otherwise (Recent empty but Starred has rows): render nothing for Recent.
  if (items.length === 0) return null;

  return (
    <>
      <div
        className={"sidebar-group-header" + (starredCount > 0 ? " with-prior" : "")}
      >
        <span className="label">Recent</span>
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          {items.length}
        </span>
      </div>

      <div ref={topSentinelRef} />

      {items.map((f) => (
        <SidebarFlavorRow
          key={f.id}
          flavor={{
            id: String(f.id),
            name: f.name,
            updated: formatRelative(f.updatedAt),
          }}
          active={activeId === String(f.id)}
          onSelect={onSelect}
        />
      ))}

      <div ref={bottomSentinelRef} />

      {q.isFetchingNextPage && (
        <div className="mono sidebar-empty">Loading…</div>
      )}
    </>
  );
}
